import * as XLSX from 'xlsx';
import { complianceTypeLabel } from '../compliance-ui/complianceIssuePresenters';
import { downloadBlob, escapeHtml, wrapExportDocument, exportToHTMLFile } from '../compliance-ui/complianceExport';

export const REQUIREMENTS_EXPORT_COLUMNS = [
  { key: 'article',         label: 'Ref #',            locked: false },
  { key: 'requirementText', label: 'Requirement Text', locked: true  },
  { key: 'complianceType',  label: 'Compliance Type',  locked: false },
  { key: 'subcategory',     label: 'Subcategory',      locked: false },
  { key: 'sourceDocName',   label: 'Source Document',  locked: false },
  { key: 'pageNumber',      label: 'Page',             locked: false },
];

function getRequirementCellValue(item, col) {
  switch (col) {
    case 'article':         return item.article || '—';
    case 'requirementText': return item.requirementText || '';
    case 'complianceType':  return complianceTypeLabel(item.complianceType) || item.complianceType || '';
    case 'subcategory':     return item.subcategory || item.compliance_subcategory || '—';
    case 'sourceDocName':   return item.sourceDocName || '';
    case 'pageNumber':      return item.pageNumber > 0 ? item.pageNumber : '';
    default:                return item[col] || '';
  }
}

export function runRequirementsExport({ format, data, columns, projectName = '', includeHeader = true }) {
  const list = data || [];
  if (!list.length) {
    alert('No requirements to export.');
    return;
  }

  const cols = columns?.length ? columns : REQUIREMENTS_EXPORT_COLUMNS.map(c => c.key);
  const timestamp = new Date().toISOString().split('T')[0];
  const safeProject = (projectName || 'project').replace(/[^\w-]+/g, '_').slice(0, 40);
  const base = `requirements_${safeProject}_${timestamp}`;

  const labelByKey = Object.fromEntries(REQUIREMENTS_EXPORT_COLUMNS.map(c => [c.key, c.label]));
  const headers = cols.map(col => labelByKey[col] || col);
  const rows = list.map(item => cols.map(col => getRequirementCellValue(item, col)));
  const aoa = [headers, ...rows];

  if (format === 'csv') {
    const csv = aoa
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `${base}.csv`);
    return;
  }

  if (format === 'excel') {
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    worksheet['!cols'] = headers.map((h, i) => ({
      wch: Math.min(80, Math.max(10, Math.max(h.length, ...rows.map(r => String(r[i] ?? '').length)) + 2)),
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Requirements');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    downloadBlob(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `${base}.xlsx`
    );
    return;
  }

  if (format === 'html') {
    const headerRow = headers.map(h => `<th>${escapeHtml(h)}</th>`).join('');
    const bodyRows = rows
      .map(r => `<tr>${r.map(v => `<td>${escapeHtml(String(v ?? ''))}</td>`).join('')}</tr>`)
      .join('');
    const html = wrapExportDocument({
      title: 'Requirements Register',
      subtitle: `${projectName ? `${escapeHtml(projectName)} · ` : ''}${list.length} requirement${list.length !== 1 ? 's' : ''} · ${new Date().toLocaleString()}`,
      body: `<div class="table-wrap"><table><thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table></div>`,
      variant: 'table',
    });
    exportToHTMLFile(html, `${base}.html`);
  }
}